import type {
  AudienceEstimate,
  BroadcastInput,
  MerchantOption,
  SentNotification,
} from "./types";

// In-memory mock of the sent-notifications history + audience estimates, used in LOCAL
// (VITE_USE_MOCK). It demoes the full UI — including SMS/BOTH, scheduling, single-recipient
// (merchant picker), cancelling, and drafts. Recipient labels for newly-sent rows are Uzbek
// free-text (the operator UI is Uzbek-first); every other column (channel, status, dates)
// renders bilingually via i18n.

// Tag a thrown error with a stable code; api.ts normalises it into a NotificationError.
function notifError(code: string): Error & { notifCode: string } {
  const err = new Error(code) as Error & { notifCode: string };
  err.notifCode = code;
  return err;
}

// Total merchant base, in sync with the "Barcha mijozlar · 64 280" recipient pill.
const TOTAL_CUSTOMERS = 64280;

// Seeded sent-history (newest first). Covers every status so the table renders all badges. Each row
// carries both languages (task 0081); the table shows whichever matches the dashboard's i18n language.
let items: SentNotification[] = [
  {
    id: 5,
    titleUz: "Yangi chegirma — qoralama",
    titleRu: "Новая скидка — черновик",
    recipients: "Barcha mijozlar",
    channel: "push",
    delivered: null,
    date: "2026-06-15T08:20:00Z",
    status: "draft",
  },
  {
    id: 4,
    titleUz: "To‘lov eslatmasi",
    titleRu: "Напоминание об оплате",
    recipients: "Barcha mijozlar",
    channel: "push",
    delivered: 3240,
    date: "2026-06-13T09:00:00Z",
    status: "sent",
  },
  {
    id: 3,
    titleUz: "Yangi imkoniyat: Pro",
    titleRu: "Новая возможность: Pro",
    recipients: "Faol · Premium",
    channel: "both",
    delivered: 8910,
    date: "2026-06-11T12:30:00Z",
    status: "sent",
  },
  {
    id: 2,
    titleUz: "Bayram tabrigi",
    titleRu: "Поздравление с праздником",
    recipients: "Barcha mijozlar",
    channel: "push",
    delivered: null,
    date: "2026-06-14T10:00:00Z",
    status: "scheduled",
  },
  {
    id: 1,
    titleUz: "Kechikkan to‘lov",
    titleRu: "Просроченный платёж",
    recipients: "Nofaol",
    channel: "sms",
    delivered: 1198,
    date: "2026-06-08T15:00:00Z",
    status: "sent",
  },
  {
    id: 0,
    titleUz: "Tizim ishlari — bekor qilingan",
    titleRu: "Технические работы — отменено",
    recipients: "Barcha mijozlar",
    channel: "push",
    delivered: null,
    date: "2026-06-12T06:00:00Z",
    status: "cancelled",
  },
];

let nextId = 6;

// A handful of merchants for the SINGLE picker (Uzbek names; organic client counts and
// +998 9X numbers). mockSearchMerchants filters this by name/phone, mirroring the backend
// ?search= over first_name/last_name/phone_number.
const MERCHANTS: MerchantOption[] = [
  { id: 41, firstName: "Dilshod", lastName: "Rahimov", phoneNumber: "+998 90 247-18-63", clientCount: 312 },
  { id: 58, firstName: "Nigora", lastName: "Yusupova", phoneNumber: "+998 93 514-09-27", clientCount: 87 },
  { id: 63, firstName: "Sardor", lastName: "Aliyev", phoneNumber: "+998 91 332-76-40", clientCount: 1043 },
  { id: 77, firstName: "Kamola", lastName: "Tojiboyeva", phoneNumber: "+998 94 108-55-12", clientCount: 29 },
  { id: 92, firstName: "Bekzod", lastName: "Ergashev", phoneNumber: "+998 97 641-23-08", clientCount: 458 },
  { id: 105, firstName: "Mavluda", lastName: "Qodirova", phoneNumber: "+998 88 720-94-31", clientCount: 6 },
];

// Uzbek labels for mock history rows (mirrors the segment-filter status options).
const STATUS_UZ: Record<string, string> = {
  segStatusAll: "Barcha",
  segStatusActive: "Faol",
  segStatusInactive: "Nofaol",
  segStatusNew: "Yangi",
};

// How much each segment filter narrows the base (rough, deterministic mock estimate).
const STATUS_FACTOR: Record<string, number> = {
  segStatusAll: 1,
  segStatusActive: 0.7,
  segStatusInactive: 0.3,
  segStatusNew: 0.18,
};
// "Faqat Premium" narrows the chosen status set further.
const PREMIUM_FACTOR = 0.22;

// Audience estimate shown in the right-hand stats card. Pure + deterministic so it can be
// called on every keystroke. Mock-only: real mode hides this (no segment-size endpoint) and
// shows the true recipient_count in the history table instead — see api.supportsEstimate.
export function estimateAudience(input: BroadcastInput): AudienceEstimate {
  if (input.recipientMode === "single") return { total: 1, reach: 1 };
  if (input.recipientMode === "segment") {
    const s = STATUS_FACTOR[input.customerStatus ?? "segStatusAll"] ?? 1;
    const p = input.segmentPremium ? PREMIUM_FACTOR : 1;
    const total = Math.round(TOTAL_CUSTOMERS * s * p);
    return { total, reach: Math.round(total * 0.95) };
  }
  return { total: TOTAL_CUSTOMERS, reach: 61200 };
}

// Display label for the recipients column of a freshly-sent mock row.
function recipientsLabel(input: BroadcastInput): string {
  if (input.recipientMode === "all") return "Barcha mijozlar";
  if (input.recipientMode === "single") return input.query?.trim() || "Bitta mijoz";
  const parts: string[] = [];
  const status = STATUS_UZ[input.customerStatus ?? "segStatusAll"];
  if (input.customerStatus && input.customerStatus !== "segStatusAll" && status) parts.push(status);
  if (input.segmentPremium) parts.push("Premium");
  return parts.length ? parts.join(" · ") : "Segment";
}

export function mockListNotifications(): SentNotification[] {
  // Return copies so callers can't mutate the in-memory store.
  return items.map((i) => ({ ...i }));
}

export function mockSendBroadcast(input: BroadcastInput): SentNotification {
  const titleUz = input.titleUz.trim();
  const titleRu = input.titleRu.trim();
  const textUz = input.textUz.trim();
  const textRu = input.textRu.trim();
  // Mirror the backend guard: both languages are mandatory to SEND (task 0081).
  if (!titleUz || !titleRu || !textUz || !textRu) throw notifError("contentRequired");

  const scheduled = input.sendMode === "schedule";
  const created: SentNotification = {
    id: nextId++,
    titleUz,
    titleRu,
    recipients: recipientsLabel(input),
    channel: input.channel,
    delivered: scheduled ? null : estimateAudience(input).reach,
    // A scheduled row carries its future send time; an immediate send is stamped now.
    date: scheduled && input.scheduledAt ? input.scheduledAt : new Date().toISOString(),
    status: scheduled ? "scheduled" : "sent",
  };
  items = [created, ...items];
  return { ...created };
}

// SINGLE picker search: case-insensitive match over name + phone, mirroring the backend's
// ?search= on first_name/last_name/phone_number. Empty query returns the full list.
export function mockSearchMerchants(query: string): MerchantOption[] {
  const q = query.trim().toLowerCase();
  if (!q) return MERCHANTS.map((m) => ({ ...m }));
  return MERCHANTS.filter((m) =>
    `${m.firstName} ${m.lastName} ${m.phoneNumber}`.toLowerCase().includes(q),
  ).map((m) => ({ ...m }));
}

// Cancel a still-pending scheduled row → CANCELLED (no-op if it isn't scheduled), mirroring
// the backend cancel action. The list refetch then re-renders it with the cancelled badge.
export function mockCancelScheduled(id: number): void {
  items = items.map((i) =>
    i.id === id && i.status === "scheduled" ? { ...i, status: "cancelled" } : i,
  );
}
